import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import { claims, isKeycloakConfigured, logout as keycloakLogout } from './keycloak'

export interface Organization {
  id: string
  name: string
}

export interface Me {
  userId: string
  userName: string
  email: string | null
  displayName: string | null
  roles: string[]
  organizations: Organization[]
  activeOrganization: Organization | null
}

interface AuthState {
  me: Me | null
  organizations: Organization[]
  activeOrg: Organization | null
  /** True when the user belongs to several organizations and has not chosen one. */
  mustChooseOrganization: boolean
  /** True when the user belongs to no organization at all. */
  hasNoOrganization: boolean
  loading: boolean
  error: string | null
  setActiveOrg: (id: string) => void
  hasRole: (...roles: string[]) => boolean
  logout: () => void
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * Active-organization storage key, scoped to the user.
 *
 * Scoping matters: a shared or reused browser would otherwise carry one user's
 * selection into the next user's session, and every screen would render another
 * organization's numbers under their name.
 */
const ACTIVE_ORG_KEY_PREFIX = 'tracer.activeOrg:'

function storageKey(userId: string | undefined): string | null {
  return userId ? `${ACTIVE_ORG_KEY_PREFIX}${userId}` : null
}

/**
 * The organization id the API should act under for the current request.
 *
 * Read by the fetch client. Module-level rather than context state because the fetch
 * client is not a React component and must not need one.
 */
let currentOrgId: string | null = null

export function getActiveOrganizationId(): string | null {
  return currentOrgId
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [activeOrg, setActiveOrgState] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const applyActiveOrg = useCallback((org: Organization | null, userId?: string) => {
    setActiveOrgState(org)
    currentOrgId = org?.id ?? null

    const key = storageKey(userId)
    if (key) {
      if (org) {
        localStorage.setItem(key, org.id)
      } else {
        localStorage.removeItem(key)
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        // /api/auth/me is exempt from the organization gate, so it answers even when
        // the user has not selected one yet — which is how we learn they must.
        const profile = await api.get<Me>('/api/auth/me')
        if (cancelled) return

        setMe(profile)

        // The token is the source of truth for membership. A stored selection is only
        // honoured if it is still one of the user's organizations — being removed from
        // a group must take effect immediately, not at the next login.
        const stored = localStorage.getItem(storageKey(profile.userId) ?? '') ?? null
        const restored = stored
          ? (profile.organizations.find((o) => o.id === stored) ?? null)
          : null

        const resolved =
          restored ??
          profile.activeOrganization ??
          (profile.organizations.length === 1 ? profile.organizations[0]! : null)

        applyActiveOrg(resolved, profile.userId)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [applyActiveOrg])

  const setActiveOrg = useCallback(
    (id: string) => {
      const match = me?.organizations.find((o) => o.id === id)
      if (!match || !me) return
      applyActiveOrg(match, me.userId)
    },
    [me, applyActiveOrg],
  )

  const hasRole = useCallback(
    (...roles: string[]) => roles.some((r) => me?.roles.includes(r) ?? false),
    [me],
  )

  const value = useMemo<AuthState>(
    () => ({
      me,
      organizations: me?.organizations ?? [],
      activeOrg,
      mustChooseOrganization: (me?.organizations.length ?? 0) > 1 && activeOrg === null,
      hasNoOrganization: me !== null && me.organizations.length === 0,
      loading,
      error,
      setActiveOrg,
      hasRole,
      logout: () => {
        // Clear the selection before leaving so a shared browser does not carry it into
        // the next session.
        applyActiveOrg(null, me?.userId)
        keycloakLogout()
      },
    }),
    [me, activeOrg, loading, error, setActiveOrg, hasRole, applyActiveOrg],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }
  return context
}

/**
 * Identity fingerprint for data-fetch cache keys.
 *
 * Including it in every query key is what makes switching organizations refetch rather
 * than showing the previous organization's cached numbers — the failure mode that is
 * hardest to notice, because the screen looks entirely normal.
 */
export function useIdentityFingerprint(): string {
  const { me, activeOrg } = useAuth()
  return `${me?.userId ?? 'anon'}|${activeOrg?.id ?? 'none'}`
}

/** Claims straight off the token, for debugging. */
export function tokenClaims() {
  return isKeycloakConfigured() ? claims() : null
}
